import * as R from "ramda"
import {Action} from "redux";

export const formatInt = (a: number) => formatFloat(a, 0)
export const formatFloat = (a: number, len: number) => a.toLocaleString('en-AU', {minimumFractionDigits: len, maximumFractionDigits: len})

export const sumReducer = (a: number, b: number): number => a + b
export const sum = (a: number[]): number => a.reduce((a, b) => a + b, 0)
export const isNumeric = (number: string): boolean => number === `${Number.parseInt(number)}`

export const dateToTimestamp = (d: Date | null) => d !== null ? Math.floor(d.getTime()/1000) : 0
export const curryFunc = <T1,T2,R>(f:(t1:T1,t2:T2) => R) => (t1:T1) => (t2:T2) => f(t1, t2)

export const substring = (len:number,s:string) => s.substr(0,len)
export const just = curryFunc(substring)
export const just3 = just(3)

export const mapSubtract = <T,U>(source:Immutable.OrderedMap<T,U>, exclusions:Immutable.OrderedMap<T,U>):Immutable.OrderedMap<T,U> => {
	return source.filter(
		(v?:U,k?:T) => !!k && !exclusions.has(k)
	) as Immutable.OrderedMap<T,U>
}

export const mapKeys = <T,U>(map:Immutable.OrderedMap<T,U>) => {
	return map.map((v,k) => String(k)).toArray()
}

export const mapNonNull = <T, U>(t: T | null | undefined, f: (x: T) => U, def?: U) => t == null ? def : f(t)
export const mapClass = <T1, T extends T1, U>(t: T1 | null | undefined, constructor: new() => T, f: (x: T) => U, def?: U) => t instanceof constructor ? f(t) : def

export function curriedCompose<A, R>(applyAfter:(a:A) => R) {
	function takeMethodToApplyFirst<T1>(f: (t1: T1) => A): (t1: T1) => R
	function takeMethodToApplyFirst<T1, T2>(f: (t1: T1, t2: T2) => A): (t1: T1, t2: T2) => R
	function takeMethodToApplyFirst<T1, T2>(f: (t1: T1, t2: T2) => A) {
		return R.compose(applyAfter, f)
	}
	return takeMethodToApplyFirst
}

export function unzip<T, U>(val: [T, U][]): [T[], U[]] {
	return val.reduce<[T[], U[]]>((acc: [T[], U[]], v: [T, U]) => [acc[0].concat([v[0]]), acc[1].concat([v[1]])], [[], []])
}

export const compareStringsNumerically = (string1: string, string2: string) => {
	const cat1Id = parseInt(string1)
	const cat2Id = parseInt(string2)
	if (isNaN(cat1Id) || isNaN(cat2Id))
		throw new Error(`expected two numerical strings, got ${string1} and ${string2}`)

	return compare(cat1Id, cat2Id)
}

export const compareStringsAlphabetically = (string1: string, string2: string) => {
	return compare(string1, string2)
}

const compare = <T extends string|number>(item1: T, item2: T) => {
	if (item1 < item2) {
		return -1
	} else if (item1 > item2) {
		return 1
	} else {
		return 0
	}
}
/**
 * Combines multiple different strings together, ignoring values of null or zero length.
 */
export const joinStrings = (names:string[], separator:string = " "):string =>
	names.filter((name) => name != null && name.length > 0).join(separator)

export const splitStringByLengths = (str: string, groupLengths: number[]): string[] => {
	const extractGroup = (groupLength: number, i: number) => {
		const start = R.sum(groupLengths.slice(0, i))
		return str.substr(start, groupLength)
	}
	return groupLengths.map(extractGroup).filter(R.identity)
}

export const debug = <A>(a:A, ...b:any[]) => (console.log(a, ...b), a)

/**
 * Intended to assist in the filtering of JS bugsnag errors in future
 */
export class IVTError extends Error {}

export const removeNulls = <T>(aa: (T|null|undefined)[]): T[] => {
	const result: T[] = []
	aa.forEach(a => {if (a != null) result.push(a)})

	return result;
}

export const getElementByIdOrThrow = (document: Document, id: string) => {
	const element = document.getElementById(id)
	if (element === null) {
		throw new Error("couldn't find element with id " + id)
	}
	return element
}

export const callIfRightType: <T, S extends T, R1, R2 extends R1>(narrower: (n: T) => n is S) => (f: (s: S) => R2, def: R1) => (s: T) => R1
	= (narrower) => (f, def) => (s) => narrower(s) ? f(s) : def
export const callIfRightTypeUndef: <T, S extends T, R>(narrower: (n: T) => n is S) => (f: (s: S) => R) => (s: T) => R|undefined
	= <T, S extends T, R>(narrower: (n: T) => n is S) => (f: (s: S) => R) => callIfRightType<T, S, R|undefined, R>(narrower)(f, undefined)
/**
 * `makeReducer` is like `callIfRightType`, except that the f takes two parameters.
 * The first parameter is the default return value, and it is the second parameter that is checked for specialisation.
 */
export const makeReducer: <T, S extends T, R>(narrower: (s: T) => s is S, reducer: (s0: R, s: S) => R, initialState: R) => (s0: R, s: T) => R
	= (narrower, reducer, initialState) => (state = initialState, action) => narrower(action) ? reducer(state, action) : state

export const callOrVoid = <T, S extends T, R>(narrower: (n: T) => n is S) => (f: (s: S) => R): (s: T) => R|undefined => callIfRightType<T, S, R|undefined, R>(narrower)(f, undefined)

export const getKeys = <T>(o: {[I in keyof T]: any}): (keyof T)[] => Array.from(Object.keys(o)) as (keyof T)[]
export const mapObjectKeys = <T, B, C>(o: {[I in keyof T]: B}, f: (key: keyof  T) => C): C[] => getKeys(o).map(f)
export const mapValues = <ParameterItem, ReturnItem, Parameter extends {[index: string]: ParameterItem}>(
	o: Parameter,
	f: (value: ParameterItem, key: keyof Parameter) => ReturnItem
): ReturnItem[] => getKeys(o).map(key => f(o[key], key))

export const filterObjectArray = <A extends {[P in B]: C}, B extends keyof A, C>(data: A[], key: B, test: C): A[] => data.filter(d => d[key] == test)

//values must be in both `a` and `b`. Uses `default` if not found in `b`.
//sorry folks, i need a type annotation in this function so I can't make it legible. typescript could've done with a better syntax
export const mergeArraysByValue: <A, B, R>(merge:  (a: A, b: B) => R, areMergeable: (a: A) => (b: B, index: number) => boolean, defaultB?: B|undefined) => (values: A[], callbacks: B[]) => R[]
	= <A, B, R>(merge: (a: A, b: B) => R, areMergeable: (a: A) => (b: B, index: number) => boolean, defaultB: B|undefined) =>
		(values: A[], callbacks: B[]): R[] =>
		values.reduce((acc: R[], v) => {
	const callback = callbacks.find(areMergeable(v)) || defaultB
	return callback ? acc.concat([merge(v, callback)]) : acc
}, [])

export const pad: (len: number, padding: string) => (str: string) => string
	= (len, padding) => (strToPad) => strToPad.length < len ? padding.repeat(len - strToPad.length) + strToPad : strToPad
export const twoDigitNumber = (num: number) => pad(2, '0')(num.toString())
export const dateToYYYYMMDD: (date: Date) => string
	= date => date.getFullYear() + "-" + twoDigitNumber(date.getMonth() + 1) + "-" + twoDigitNumber(date.getDate())

export const concat = <A>(start: A[], end: A[]) => start.concat(end)
export const flatten = <A>(as: A[][]): A[] => as.reduce(concat, [])
export const unique = <A>(withDupes: A[]): A[] => Array.from(new Set(withDupes))

export const compose = <A, B, R>(f: (a: A) => B, g: (b: B) => R) => (a: A): R => {
	return g(f(a))
}

export type Dispatcher<A extends Action, S> = DispatcherActionLike<A, A> & DispatcherAsync<A, S>
export type DispatcherActionLike<B, R> = (b: B) => R
export type DispatcherAsync<A, S> = <R>(asyncAction: DispatchableAsync<A, S, R>) => R
export type DispatchableAsync<A, S, R> = (dispatch: DispatcherActionLike<A, R>, getState: () => S) => R

export const withDefault = <A, B, R>(f: (a: A, b: B) => R, def: A) => (a: A|undefined, b: B): R => f(a === undefined ? def : a, b) || def

export const dataFromSerial = <T extends {[P in B]: string}, B extends keyof T>(id: string, map: T[], def: string, searchKey: B, key: B): string => {
	const data = map.find(m => m[searchKey] == id)
	return data ? data[key] : `${def} no. ${id}`
}

export const secsToHMS = (input: number) => {
	return [
		input / 3600,
		input % 3600 / 60,
		input % 60,
	].map(a => twoDigitNumber(Math.floor(a))).join(':')
}

/**
 * `A` is the error type by a pre-existing convention, and established in labels here.
 * `B` is the success type by logical implication.
 */
type Either<A, B> = {type: 'error', error: A}|{type: 'success', success: B}

export const objToList = <A extends {[index:string]: ValueIn}, ValueIn, ValueOut>(map: (v:ValueIn) => ValueOut, obj: A): ValueOut[] => {
	const list: ValueOut[] = []
	for (const key of Object.keys(obj)) {
		list.push(map(obj[key]))
	}

	return list
}

export const findInObj = <A extends {[index:string]: T}, T>(predicate: (t:T) => boolean, obj: A): T|undefined => {
	return objToList<A, T, T>((t) => t, obj).find(predicate)
}

export const reduceToObj = <K extends string, ValueIn, ValueOut, Result extends {[key in K]: ValueOut} = {[key in K]: ValueOut}>(key: (v:ValueIn) => K, value: (v:ValueIn) => ValueOut, data: ValueIn[]):Result => {
	const accumulator = {} as Result
	return data.reduce<Result>((acc, v) => {
		acc[key(v)] = value(v)
		return acc
	}, accumulator)
}

export const mapObj =
	<A extends {[index: string]: ValueIn}, ValueIn, ValueOut>(map: (v: ValueIn) => ValueOut, obj: A):
		{[K in keyof A]: ValueOut} => {
		//to begin, we lie. i deeply apologise but i have not yet determined an alternative
		const ret = {} as {[K in keyof A]: ValueOut}
		for (const key of Object.keys(obj)) {
			ret[key] = map(obj[key])
		}

		return ret
	}

export const reduceObj = <ValueIn, ValueOut, A extends {[index: string]: ValueIn}>(reduce: (k: string, v: ValueIn, acc: ValueOut) => ValueOut, obj: A, accumulator: ValueOut): ValueOut => {
	for (const key of Object.keys(obj)) {
		accumulator = reduce(key, obj[key], accumulator)
	}
	return accumulator
}

export const mapObjEither =
	<A extends {[index: string]: ValueIn}, ValueIn, ValueOut, Error>(map: (v: ValueIn) => Either<Error, ValueOut>, obj: A):
		Either<Error, {[K in keyof A]: ValueOut}> => {
	//to begin, we lie. i deeply apologise but i have not yet determined an alternative
	const ret = {} as {[K in keyof A]: ValueOut}
	for (const key of Object.keys(obj)) {
		const val = map(obj[key])
		if(val.type == "error") {
			return val
		} else {
			ret[key] = val.success
		}
	}

	return {type: 'success', success: ret}
}

const tryParseInt = (s: string): Either<string, number> => {
	const n = Number.parseInt(s)
	return isNaN(n)
		? {type: 'error', error: s}
		: {type: 'success', success: n}
}

export const toInts = <A extends {[index: string]: string}>(obj: A): {[K in keyof A]: number}|null => {
	const v = mapObjEither(tryParseInt, obj)
	return v.type == "success" ? v.success : null
}

export const maybeToInts = <A extends {[index: string]: string|null}>(obj: A): {[K in keyof A]: number|null}|null => {
	const v = mapObjEither<A, string|null, number|null, string>(a => a !== null ? tryParseInt(a) : {type: "success", success: null}, obj)

	return v.type == "success" ? v.success : null
}

export const range = (first: number, last: number): number[] => {
	const range = (l:number,r:number):number[] => new Array(r - l + 1).fill(null).map((v,k) => k + l)
	return last > first ? range(first,last) : range(last,first)
}

export const max = (a: number, b: number): number => a > b ? a : b
export const min = (a: number, b: number): number => a < b ? a : b

const unkeys = <K extends string>(keys: K[]): {[P in K]: never[]} => {
	const a: {[P in K]?: never[]} = {}
	for (const key of keys) {
		a[key] = []
	}
	//we cast the type because we had to construct it over time.
	return a as {[P in K]: never[]}
}

export const zipObj = <A extends {}, K extends keyof A>(as: A[], keys: K[]): {[P in K]: A[P][]} => {
	const merge = (acc: {[P in K]: A[P][]}, curr: A): {[P in K]: A[P][]} => {
		for (const key of keys) {
			acc[key].push(curr[key])
		}
		return acc
	}

	return as.reduce(merge, unkeys(keys))
}

export const toString = (a: string|number) => "" + a
export const toStrings = (as: (string|number)[]) => as.map(toString)

export const concatenate = function*<T>(a: Iterable<T>, b: Iterable<T>) {
	for(const one of a) yield one
	for(const one of b) yield one
}

export const group = function*<T>(source: Iterable<T>, length: number) {
	let res = []
	for(const one of source) {
		res.push(one)
		if(res.length >= length) {
			yield res
			res = []
		}
	}

	while(res.length < length) {
		res.push(undefined)
	}

	yield res
}




// WEBPACK FOOTER //
// ./src/lib/utils.ts